import { ReactNode, useState, useRef, useEffect } from 'react';
import { cn } from '@/app/components/ui/utils';

interface ResizableLayoutProps {
  leftSidebar: ReactNode;
  mainContent: ReactNode;
  rightSidebar: ReactNode;
  showLeftSidebar: boolean;
}

// LocalStorage keys
const LEFT_WIDTH_KEY = 'resizable-layout-left-width';
const RIGHT_WIDTH_KEY = 'resizable-layout-right-width';

export function ResizableLayout({ 
  leftSidebar, 
  mainContent, 
  rightSidebar,
  showLeftSidebar 
}: ResizableLayoutProps) {
  // Initialize from localStorage or defaults
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = localStorage.getItem(LEFT_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : 320;
  });
  const [rightWidth, setRightWidth] = useState(() => {
    const saved = localStorage.getItem(RIGHT_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : 320;
  });
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Save to localStorage when widths change
  useEffect(() => {
    localStorage.setItem(LEFT_WIDTH_KEY, leftWidth.toString());
  }, [leftWidth]);

  useEffect(() => {
    localStorage.setItem(RIGHT_WIDTH_KEY, rightWidth.toString());
  }, [rightWidth]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();

      if (isResizingLeft) {
        const newWidth = e.clientX - containerRect.left;
        if (newWidth >= 240 && newWidth <= 560) { // Min 240px, Max 560px
          setLeftWidth(newWidth);
        }
      }

      if (isResizingRight) {
        const newWidth = containerRect.right - e.clientX;
        if (newWidth >= 240 && newWidth <= 560) { // Min 240px, Max 560px
          setRightWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizingLeft(false);
      setIsResizingRight(false);
    };

    if (isResizingLeft || isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingLeft, isResizingRight]);

  return (
    <>
      {/* Mobile Layout */}
      <div className="lg:hidden flex-1 flex overflow-hidden relative">
        {/* Mobile: Absolute Overlay for Left Sidebar */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 h-full z-20 w-80 shadow-xl transition-transform duration-300 ease-in-out",
            showLeftSidebar ? "translate-x-0" : "-translate-x-full"
          )}
          style={{
            backgroundColor: 'var(--md-surface-container)',
            borderColor: 'var(--md-outline-variant)'
          }}
        >
          <div className="h-full flex flex-col border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
            {leftSidebar}
          </div>
        </div>

        {/* Mobile: Main Content */}
        <div className="flex-1 relative overflow-hidden flex flex-col">
          {mainContent}
        </div>

        {/* Mobile: Right Sidebar */}
        <div className="w-80 h-full overflow-hidden border-l" style={{ borderColor: 'var(--md-outline-variant)' }}>
          {rightSidebar}
        </div>
      </div>

      {/* Desktop: Custom Resizable Layout */}
      <div ref={containerRef} className="hidden lg:flex flex-1 overflow-hidden relative">
        {/* Left Sidebar */}
        {showLeftSidebar && (
          <>
            <div 
              className="h-full flex flex-col border-r transition-all duration-150"
              style={{
                width: `${leftWidth}px`,
                backgroundColor: 'var(--md-surface-container)',
                borderColor: 'var(--md-outline-variant)'
              }}
            >
              <div className="h-full overflow-auto">
                {leftSidebar}
              </div>
            </div>
            
            {/* Left Resize Handle */}
            <div
              className={cn(
                "w-1 h-full cursor-col-resize transition-all duration-150 hover:w-1.5 hover:bg-blue-500 active:bg-blue-600",
                isResizingLeft ? "bg-blue-600 w-1.5" : "bg-gray-300"
              )}
              onMouseDown={() => setIsResizingLeft(true)}
              title="Drag to resize"
            />
          </>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 h-full relative overflow-hidden flex flex-col">
          {mainContent}
        </div>

        {/* Right Resize Handle */}
        <div
          className={cn(
            "w-1 h-full cursor-col-resize transition-all duration-150 hover:w-1.5 hover:bg-blue-500 active:bg-blue-600",
            isResizingRight ? "bg-blue-600 w-1.5" : "bg-gray-300"
          )}
          onMouseDown={() => setIsResizingRight(true)}
          title="Drag to resize"
        />

        {/* Right Sidebar */}
        <div 
          className="h-full border-l transition-all duration-150"
          style={{
            width: `${rightWidth}px`,
            borderColor: 'var(--md-outline-variant)'
          }}
        >
          <div className="h-full overflow-auto">
            {rightSidebar}
          </div>
        </div>
      </div>
    </>
  );
}